/**
 * People (C5, decision 844; help `cloud.staff` and `cloud.users-roles`): the staff register and the sign-in accounts
 * on one page. Every staff role sees "Staff register" (as the Staff page was); tenant admins also see "Sign-in and
 * roles" (as Users & roles was). Client admins keep their own Users page, Braynewave's console its Users & roles.
 * Display only: RLS and the Edge Functions decide what each person reads and changes.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { PageHeader } from '../components/States.tsx';
import { TabPanel, Tabs } from '../components/Tabs.tsx';
import { StaffScreen } from '../staff/StaffScreen.tsx';
import { UsersScreen } from '../users/UsersScreen.tsx';

export type PeopleTab = 'staff' | 'signin';

export interface PeopleScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  /** Where invitation links land (this portal's address). */
  readonly redirectTo: string;
  /** The tab it opens on (a link to the old Users & roles opens "signin"). */
  readonly initialTab?: string | null;
  /** The tab shown (the help panel follows it). */
  readonly onTabChange?: (tab: PeopleTab) => void;
}

export function peopleTabs(roles: readonly string[]): ReadonlyArray<readonly [PeopleTab, string]> {
  return [
    ['staff', 'Staff register'],
    ...(roles.includes('tenant_admin') ? ([['signin', 'Sign-in and roles']] as const) : []),
  ];
}

export function PeopleScreen({
  sb,
  tenantId,
  userId,
  roles,
  redirectTo,
  initialTab,
  onTabChange,
}: PeopleScreenProps): ReactElement {
  const tabs = peopleTabs(roles);
  const [tab, setTab] = useState<PeopleTab>(() => tabs.find(([id]) => id === initialTab)?.[0] ?? 'staff');
  useEffect(() => onTabChange?.(tab), [tab, onTabChange]);
  return (
    <section className="page">
      <PageHeader
        title="People"
        subtitle={
          tabs.length > 1
            ? 'Everyone in the organisation, and who can sign in to Teslyn with which roles.'
            : 'Everyone in the organisation, with their roles.'
        }
      />
      {tabs.length > 1 && (
        <Tabs label="People sections" tabs={tabs} current={tab} onChange={setTab} idPrefix="people" />
      )}
      <TabPanel idPrefix="people" current={tab}>
        {tab === 'signin' ? (
          <UsersScreen sb={sb} tenantId={tenantId} currentUserId={userId} redirectTo={redirectTo} embedded />
        ) : (
          <StaffScreen sb={sb} tenantId={tenantId} roles={roles} redirectTo={redirectTo} embedded />
        )}
      </TabPanel>
    </section>
  );
}
